export interface Bank {
  id: string
  name: string
  single_use?: boolean | number | null
}

export interface IssuingBankPayment {
  issuing_bank?: string | null
  issuing_bank_resource?: { name?: string | null } | null
  origin_bank_account?: string | null
}

/**
 * The banks a cheque can be issued by: the shared list, minus the banks created
 * for a single payment, sorted by name.
 */
export function selectableBanks(banks: Bank[] | null | undefined): Bank[] {
  return (banks ?? [])
    .filter((bank) => !bank.single_use)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
}

/**
 * The issuing bank to show for a payment: the bank picked in the list, otherwise the
 * name typed when the cheque was recorded.
 */
export function issuingBankName(
  payment: IssuingBankPayment | null | undefined,
  banks: Bank[] | null | undefined = []
): string {
  if (!payment) return ''

  const fromList =
    payment.issuing_bank_resource?.name ??
    (payment.issuing_bank
      ? banks?.find((bank) => bank.id === payment.issuing_bank)?.name
      : null)

  return fromList || payment.origin_bank_account || ''
}

/**
 * Picking a bank in the list sets the bank and leaves the typed name as it is. The
 * bank loaded with the payment is dropped, or its name would shadow the new pick.
 */
export function withPickedBank(
  payment: IssuingBankPayment,
  bankId: string | null
): IssuingBankPayment {
  return {
    ...payment,
    issuing_bank: bankId || null,
    issuing_bank_resource: null
  }
}

/**
 * Typing a name sets the typed name and leaves the picked bank as it is.
 */
export function withTypedBank(
  payment: IssuingBankPayment,
  name: string | null
): IssuingBankPayment {
  return {
    ...payment,
    origin_bank_account: name || null
  }
}

/**
 * Which of the two ways to give the bank is locked: once one is used the other is
 * disabled, so the user clears the first to switch. When both are filled (a cheque
 * recorded before the list) or neither is, nothing is locked.
 */
export function issuingBankLocks(
  payment: IssuingBankPayment | null | undefined
): {
  pick: boolean
  type: boolean
} {
  const picked = Boolean(payment?.issuing_bank)
  const typed = Boolean(payment?.origin_bank_account)

  return { pick: typed && !picked, type: picked && !typed }
}

/**
 * A bank name reduced to what identifies it: no case, accents, spaces or
 * punctuation, so "Crédit  Agricole" and "credit-agricole" are the same.
 */
export function normalizeBankName(name: string | null | undefined): string {
  return (name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/**
 * The words of a bank name, normalized: "Crédit  Agricole" gives ["credit", "agricole"].
 */
function bankNameWords(name: string | null | undefined): string[] {
  return (name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

const SHORT_BANK_NAME_LENGTH = 4

/**
 * The known banks a typed name could be a duplicate of: same name once
 * normalized, or one name contained in the other (from 3 characters). A short
 * known name ("ING", "CIC") only matches as a whole word, or "Banking Group"
 * would suggest ING.
 */
export function similarBanks(
  name: string | null | undefined,
  banks: Bank[] | null | undefined
): Bank[] {
  const typed = normalizeBankName(name)
  if (typed.length < 3) return []
  const typedWords = bankNameWords(name)

  return (banks ?? []).filter((bank) => {
    const known = normalizeBankName(bank.name)
    if (known === typed) return true
    if (known.length < SHORT_BANK_NAME_LENGTH) {
      return typedWords.includes(known)
    }
    return known.includes(typed) || typed.includes(known)
  })
}
